import type { ProgrammaticLandingPageConfig } from '../../types'
import {
  STORAGE_REFERENCE_KWP,
  SELF_CONSUMPTION_WITHOUT_BATTERY,
  SELF_CONSUMPTION_BATTERY_BONUS,
  SELF_CONSUMPTION_WITH_BATTERY,
  BATTERY_COST_PER_KWH_RANGE_EUR,
} from '../storageSizeFacts'

export const KWH_5_MAGAZYN_ENERGII: ProgrammaticLandingPageConfig = {
  slug: 'magazyn-energii-5-kwh-cena',
  lang: 'pl',
  topicId: 'kwh-speicher-5',
  title: 'Magazyn energii 5 kWh: cena i wartości orientacyjne 2026',
  metaDescription:
    'Ile kosztuje magazyn energii 5 kWh? Dla kogo się nadaje, wartości orientacyjne ' +
    'autokonsumpcji i bezpłatne porównanie ofert na instalację + magazyn.',
  keywords: [
    'magazyn energii 5 kWh cena',
    'magazyn energii 5 kWh koszt',
    'bateria fotowoltaiczna 5 kWh',
    'ile kosztuje magazyn energii 5 kWh',
  ],
  eyebrow: 'Wielkość magazynu: 5 kWh',
  h1: 'Magazyn energii 5 kWh: cena i wartości orientacyjne',
  intro:
    'Magazyn energii 5 kWh to nasza najmniejsza indeksowana wielkość - typowe uzupełnienie dla ' +
    `mniejszych gospodarstw domowych z referencyjną instalacją ${STORAGE_REFERENCE_KWP} kWp. ` +
    'Znajdziesz tu wartości orientacyjne dotyczące odpowiedniej wielkości gospodarstwa, ' +
    'autokonsumpcji z magazynem energii, a tuż poniżej kalkulator z już wypełnioną średnią ceną ' +
    'dla tego połączenia, obliczoną na podstawie prawdziwych ofert firm rzemieślniczych.',
  calculatorPrefill: { kind: 'kwp+storage', kwp: STORAGE_REFERENCE_KWP, kwh: 5 },
  indexPolicy: 'index',
  sections: [
    { type: 'heading', text: 'Dla kogo odpowiedni jest magazyn energii 5 kWh?' },
    {
      type: 'paragraph',
      text:
        'Wartość orientacyjna: 5 kWh zwykle pasuje do gospodarstw 1-2-osobowych z niskim zużyciem ' +
        'w ciągu dnia, bez samochodu elektrycznego czy pompy ciepła. Przy wyższym zużyciu ' +
        'wieczorem, pompie ciepła lub samochodzie elektrycznym warto rozważyć większe wielkości ' +
        'magazynu.',
    },
    { type: 'heading', text: 'Autokonsumpcja z magazynem energii' },
    {
      type: 'paragraph',
      text:
        `Bez magazynu udział autokonsumpcji w instalacji ${STORAGE_REFERENCE_KWP} kWp wynosi ` +
        `zwykle ok. ${SELF_CONSUMPTION_WITHOUT_BATTERY * 100} %. Z magazynem energii udział ten ` +
        `rośnie w modelu obliczeniowym o kolejne ${SELF_CONSUMPTION_BATTERY_BONUS * 100} punktów ` +
        `procentowych, do ok. ${SELF_CONSUMPTION_WITH_BATTERY * 100} % - przy dodatkowej pompie ` +
        'ciepła lub samochodzie elektrycznym udział może dalej rosnąć, aż do granicy 85 %. ' +
        'Dokładne obliczenie dla Twojego zużycia daje kalkulator doboru instalacji.',
    },
    { type: 'heading', text: 'Zasilanie awaryjne' },
    {
      type: 'paragraph',
      text:
        'Wiele systemów magazynowania energii oferuje opcjonalnie funkcję zasilania awaryjnego ' +
        'poprzez dodatkową skrzynkę backup - przy 5 kWh pojemność zwykle wystarcza na pojedyncze ' +
        'centralne odbiorniki (lodówka, router, oświetlenie), a nie na cały dom. Czy i w jakim ' +
        'zakresie dana oferta obejmuje zasilanie awaryjne, zależy od konkretnej firmy - warto o to ' +
        'zapytać bezpośrednio.',
    },
    { type: 'heading', text: 'Koszty magazynu i dofinansowanie' },
    {
      type: 'paragraph',
      text:
        'Koszty magazynów energii mocno się różnią w zależności od producenta i złożoności ' +
        `montażu - badanie rynku z 2026 roku pokazuje przedział od ok. ${BATTERY_COST_PER_KWH_RANGE_EUR.min} ` +
        `do ${BATTERY_COST_PER_KWH_RANGE_EUR.max.toLocaleString('pl-PL')} € za kWh. W Niemczech ` +
        'istnieją regionalne dofinansowania specjalnie na magazyny energii (np. program kraju ' +
        'związkowego Berlin z dopłatą do magazynów energii); w Austrii system EAG dolicza ' +
        '150 €/kWh za magazyn - dokładne obliczenie dla Twojego połączenia daje kalkulator poniżej.',
    },
  ],
  faq: [
    {
      q: 'Czy magazyn energii 5 kWh w ogóle się opłaca?',
      a: 'Dla mniejszych gospodarstw domowych z niskim zużyciem wieczorem magazyn 5 kWh może zauważalnie zwiększyć autokonsumpcję. Przy wyższym zużyciu (pompa ciepła, samochód elektryczny) większa wielkość jest zwykle bardziej opłacalna.',
    },
    {
      q: 'Czy 5 kWh wystarczy na zasilanie awaryjne całego domu?',
      a: 'Zwykle nie - 5 kWh nadaje się raczej do pojedynczych centralnych odbiorników niż do zasilania całego domu podczas przerwy w dostawie prądu. Do szerszego zasilania awaryjnego zwykle stosuje się większe magazyny.',
    },
    {
      q: 'Z jaką wielkością instalacji łączony jest zwykle magazyn 5 kWh?',
      a: `Na tej stronie jako punkt odniesienia przyjęto instalację ${STORAGE_REFERENCE_KWP} kWp, ponieważ to najczęściej wybierana wielkość dla domów jednorodzinnych. Przy mniejszych instalacjach 5 kWh również zwykle dobrze pasuje - dokładną rekomendację dla Twojego dachu daje kalkulator doboru instalacji.`,
    },
    {
      q: 'Czy 5 kWh wystarczy na pełną samowystarczalność w nocy?',
      a: 'Przy niskim zużyciu, bez pompy ciepła i ładowania samochodu elektrycznego, często tak; przy wyższym zużyciu tylko częściowo. Dokładną ocenę dla Twojego rzeczywistego zużycia daje kalkulator doboru instalacji.',
    },
  ],
  primaryCta: { label: 'Poproś teraz o bezpłatną ofertę', action: 'customer' },
  secondaryCta: {
    label: 'Koszt instalacji fotowoltaicznej: pełny przewodnik',
    action: 'link',
    href: '/koszt-instalacji-fotowoltaicznej',
  },
  relatedLinks: [
    { label: 'Magazyn energii 7 kWh: cena i wartości orientacyjne', href: '/magazyn-energii-7-kwh-cena' },
    { label: 'Magazyn energii 10 kWh: cena i wartości orientacyjne', href: '/magazyn-energii-10-kwh-cena' },
    { label: 'Instalacja fotowoltaiczna 10 kWp: cena i wartości orientacyjne', href: '/instalacja-fotowoltaiczna-10-kwp-cena' },
    { label: 'Jak działa porównanie ofert', href: '/porownaj-oferty-fotowoltaiczne' },
  ],
}
